import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  fetchFreelancerBillingProfile,
  runFreelancerPaCheck,
  resetFreelancerPaCheck,
  initiateBillingMandateSignature,
  expireBillingMandate,
} from '@/services/freelancerBillingService'
import { useApiReady } from '@/hooks/useApiReady'
import type { BillingDraftCompany } from '@/lib/e-invoicing/billingDraftCompany'
import {
  mergeBillingDraftCompany,
  serializeBillingDraftForQueryKey,
} from '@/lib/e-invoicing/billingDraftCompany'

export const freelancerBillingProfileKeys = {
  all: ['freelancerBillingProfile'] as const,
  detail: (teamId?: string | null, draftCompany?: BillingDraftCompany | null) =>
    [
      ...freelancerBillingProfileKeys.all,
      teamId,
      serializeBillingDraftForQueryKey(draftCompany),
    ] as const,
}

export function getFreelancerBillingProfileQueryOptions(
  teamId?: string | null,
  draftCompany?: BillingDraftCompany | null,
) {
  const hasDraft = Boolean(serializeBillingDraftForQueryKey(draftCompany))

  return {
    queryKey: freelancerBillingProfileKeys.detail(teamId, draftCompany),
    queryFn: () => fetchFreelancerBillingProfile(teamId, draftCompany),
    // Settings (sans draft) : toujours relire la DB — le cache step 5 pré-onboarding
    // peut avoir isFrance=false alors que le profil freelance vient d'être persisté.
    staleTime: hasDraft ? 1000 * 60 * 5 : 0,
  }
}

export function useFreelancerBillingProfile(
  teamId?: string | null,
  draftCompany?: BillingDraftCompany | null,
  options?: { enabled?: boolean },
) {
  const apiReady = useApiReady()

  return useQuery({
    ...getFreelancerBillingProfileQueryOptions(teamId, draftCompany),
    enabled: apiReady && (options?.enabled ?? true),
  })
}

export function usePaCheck(
  draftCompany?: BillingDraftCompany | null,
  teamId?: string | null,
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (override?: Partial<BillingDraftCompany>) =>
      runFreelancerPaCheck(mergeBillingDraftCompany(draftCompany, override)),
    onSuccess: (result) => {
      queryClient.setQueryData(
        freelancerBillingProfileKeys.detail(teamId, draftCompany),
        (current: Awaited<ReturnType<typeof fetchFreelancerBillingProfile>> | undefined) => {
          if (!current) return current
          return {
            ...current,
            paStatus: result?.paStatus ?? current.paStatus,
            paLastCheckedAt: result?.paLastCheckedAt ?? current.paLastCheckedAt,
            paRoutingMetadata: result?.paRoutingMetadata ?? current.paRoutingMetadata,
            siren: result?.siren ?? current.siren,
          }
        },
      )
      void queryClient.invalidateQueries({ queryKey: ['freelancerBillingProfile'] })
    },
  })
}

export function usePaReset() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: resetFreelancerPaCheck,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['freelancerBillingProfile'] })
    },
  })
}

export function useInitiateBillingMandate(draftCompany?: BillingDraftCompany | null) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => initiateBillingMandateSignature(draftCompany),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['freelancerBillingProfile'] })
    },
  })
}

export function useExpireBillingMandate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: expireBillingMandate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['freelancerBillingProfile'] })
    },
  })
}
